import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL as API } from '../config/api';

const ComplaintsPage = () => {
  const [policyPdfUrl, setPolicyPdfUrl] = useState(null);

  useEffect(() => {
    document.title = 'Complaints Procedure | Aureate Residences Ltd';
    window.scrollTo(0, 0);
    axios.get(`${API}/admin/complaints-policy`)
      .then(r => { if (r.data.pdf_url) setPolicyPdfUrl(r.data.pdf_url); })
      .catch(() => {});
  }, []);

  const steps = [
    { n: '01', title: 'Initial contact', desc: 'Contact us within seven days of the issue arising. Provide full details of your complaint including dates, times and any relevant documentation.', time: 'Within 7 days' },
    { n: '02', title: 'Acknowledgement', desc: 'We will acknowledge your complaint within three working days and assign a dedicated case handler to investigate your concerns.', time: '3 working days' },
    { n: '03', title: 'Investigation', desc: 'Our team will investigate your complaint thoroughly, gathering all relevant information and speaking with all parties involved.', time: 'Up to 15 working days' },
    { n: '04', title: 'Resolution', desc: 'We will provide a written response detailing our findings and proposed resolution. If you remain dissatisfied, you may escalate to our senior management or to PIMS.', time: 'Within 8 weeks total' },
  ];

  return (
    <div style={{ background: 'var(--black)' }}>
      {/* Hero */}
      <section className="approach" style={{ borderTop: 0, paddingBottom: 'clamp(40px, 5vw, 64px)' }}>
        <div className="wrap">
          <div className="head">
            <span className="eyebrow">Legal</span>
            <h2>Complaints procedure.</h2>
            <p>
              At Aureate Residences Ltd we take all complaints seriously and aim to resolve them
              promptly and fairly. Our goal is to learn from any issues raised and continuously
              improve our services.
            </p>
          </div>
        </div>
      </section>

      {/* Process steps */}
      <section className="approach" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <div className="steps">
            {steps.map(s => (
              <div className="step" key={s.n}>
                <span className="n">{s.n}</span>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.1em', color: 'var(--gold)', marginTop: 8 }}>{s.time}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PDF download */}
      {policyPdfUrl && (
        <section style={{ paddingBlock: '48px 0' }}>
          <div className="wrap">
            <a
              href={policyPdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-line"
              style={{ gap: 12 }}
              data-testid="complaints-pdf-download"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              Download full complaints policy (PDF)
            </a>
          </div>
        </section>
      )}

      {/* Contact for complaints */}
      <section style={{ paddingBlock: 'clamp(60px, 8vw, 100px)' }}>
        <div className="wrap">
          <div className="head">
            <span className="eyebrow">Get in touch</span>
            <h2>How to contact us.</h2>
            <p>
              To make a complaint, please contact us using one of the methods below.
              Include as much detail as possible to help us investigate your concerns.
            </p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32, fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--soft)' }}>
            <a href="mailto:complaints@aureate-resident.com" style={{ color: 'var(--soft)', transition: 'color .25s' }} onMouseEnter={e => e.target.style.color = 'var(--gold)'} onMouseLeave={e => e.target.style.color = 'var(--soft)'}>
              complaints@aureate-resident.com
            </a>
            <a href="tel:+441313811989" style={{ color: 'var(--soft)', transition: 'color .25s' }} onMouseEnter={e => e.target.style.color = 'var(--gold)'} onMouseLeave={e => e.target.style.color = 'var(--soft)'}>
              +44 131 381 1989
            </a>
          </div>
        </div>
      </section>

      {/* External redress — PIMS */}
      <section className="founder" style={{ background: 'var(--deep)' }}>
        <div className="wrap" style={{ maxWidth: 800, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 22 }}>
          <span className="eyebrow">External redress scheme</span>
          <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 42px)' }}>Property Investors Mediation Service</h2>
          <p style={{ color: 'var(--soft)', textAlign: 'center' }}>
            If you remain dissatisfied after following our internal complaints procedure,
            you may refer your complaint to PIMS for an independent review.
            We are members of PIMS (member No.&nbsp;133801).
          </p>
          <a
            href="https://www.thepims.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-gold"
            data-testid="complaints-pims-link"
          >
            Visit PIMS &rarr;
          </a>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.1em', color: 'var(--mute)' }}>
            www.thepims.co.uk &middot; Member No. 133801
          </span>
        </div>
      </section>
    </div>
  );
};

export default ComplaintsPage;
